/**
 * RESPONSIBILITY: what a link to this site looks like when somebody shares it.
 *
 * WhatsApp, Slack, Facebook and the rest fetch the page and read its head. NONE OF THEM
 * RUNS JAVASCRIPT. Everything this site sets after it loads — its title, its icon — is
 * invisible to them, which is why a shared link said "Loading..." and showed nothing
 * else. These fields are written into the HTML before it is sent, by the server, because
 * that is the only moment a crawler is listening.
 *
 * Every field is optional, and each one already has an answer on the header: the church's
 * name and its logo. Empty means "use that", so no value is stored twice and there is
 * nothing to keep in step.
 */
import type { ImageValue } from '@framework/core';
import { Section, Text, Paragraph, Img, Link } from '@framework/core/decorators';
import { SectionName } from '../section-names';

@Section({ name: SectionName.Share, label: 'Shared links' })
export class ShareSection {
  @Text({
    label: 'Title', maxLength: 300,
    description: 'The bold line in the preview. Empty uses the church’s full name.',
  })
  title!: string;

  /**
   * Plain text, not markdown. It is read by a crawler and printed as characters, so a
   * heading or a link in it arrives as asterisks and brackets.
   */
  @Paragraph({
    label: 'Description', maxLength: 1200, rows: 5,
    description:
      'The words under the title. A preview shows the first two or three lines and cuts '
      + 'the rest, so put what matters first — the whole of it still reaches a search engine.',
  })
  description!: string;

  /**
   * The address the card NAMES, which is not always the one it was fetched from. Play
   * serves this site too, and a link to it should still say where the church lives.
   */
  @Link({
    label: 'Address to show',
    description: 'The domain printed on the card. Empty uses the address it was opened on.',
  })
  url!: string;

  @Img({
    label: 'Picture', folder: 'brand', maxSizeMb: 12, size: '1200 × 630',
    description: 'Empty uses the church’s logo. Under 600 kB, or a preview ignores it.',
  })
  image!: ImageValue;
}
